<script setup>
import { ref, computed, onMounted } from 'vue';
import { useSidebarState } from '../../composables/useSidebarState.js';
import {
    getAvailableDockActions,
    getActionByKey,
    filterValidKeys,
    serializeKeys,
    parseKeys,
    dockCategories,
    dockCategoryLabels,
    maxDockSlots,
} from './dockActions.js';
import LogoIcon from "../logo/icon.vue";

const { isEffectivelyExpanded } = useSidebarState();

const PREF_KEY = 'sidebar-dock-items';

const dockKeys = ref([]);
const showCustomizer = ref(false);

const availableDockActions = computed(() => getAvailableDockActions());
const dockItems = computed(() => dockKeys.value.map(getActionByKey).filter(Boolean));
const currentBillingPlan = computed(() => (window.myflow?.company?.billing_plan ?? '').toLowerCase());
const showUpgradeBanner = computed(() => currentBillingPlan.value === 'basic');

async function loadDockItems() {
    try {
        const raw = await _mfPrefGet(PREF_KEY);
        const { keys, changed } = filterValidKeys(parseKeys(raw));
        dockKeys.value = keys;
        if (changed) await persist();
    } catch {
        dockKeys.value = [];
    }
}

async function persist() {
    await _mfPrefSet(PREF_KEY, serializeKeys(dockKeys.value));
}

function addToDock(action) {
    if (dockKeys.value.length >= maxDockSlots) return;
    if (dockKeys.value.includes(action.key)) return;
    dockKeys.value.push(action.key);
    persist();
}

function removeFromDock(index) {
    dockKeys.value.splice(index, 1);
    persist();
}

function handleDockClick(item) {
    if (item.href) {
        window.location.href = item.href;
    } else if (item.action) {
        window.dispatchEvent(new CustomEvent('dock-action', { detail: item.action }));
    }
}

function isInDock(action) {
    return dockKeys.value.includes(action.key);
}

function actionsForCategory(category) {
    return availableDockActions.value.filter(a => a.category === category);
}

const dockCategoriesWithEntries = computed(() => {
   return dockCategories.filter(category => actionsForCategory(category).length > 0);
})

onMounted(() => {
    loadDockItems();
});
</script>

<template>
    <div class="px-2 py-2 relative group">
        <a
            v-if="showUpgradeBanner && isEffectivelyExpanded"
            :href="route('settings.company.settings')"
            v-tooltip="{ content: _mft('navigation:dock.upgrade.title'), offset: 2 }"
            class="mb-2 flex items-center rounded-xl border border-myflowLavender bg-white"
            :class="isEffectivelyExpanded ? 'gap-2 px-2.5 py-2' : 'justify-center px-0 py-2'"
        >
            <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                <logo-icon class="h-7 w-7" />
            </span>
            <span v-if="isEffectivelyExpanded" class="min-w-0 flex-1">
                <span class="block truncate text-xs font-semibold leading-4 text-myflowPurple">
                    {{ _mft('navigation:dock.upgrade.title') }}
                </span>
                <span class="block truncate text-[11px] leading-4 text-gray-500">
                    {{ _mft('navigation:dock.upgrade.subtitle') }}
                </span>
            </span>
            <i
                v-if="isEffectivelyExpanded"
                class="fa-light fa-chevron-right text-[10px] text-amber-600"
            ></i>
        </a>

        <div
            class="grid gap-1 "
            :class="isEffectivelyExpanded ? 'grid-cols-4' : 'grid-cols-4 md:grid-cols-2'"
        >
            <button
                v-for="(item, i) in dockItems"
                :key="item.key"
                v-tooltip="{ content: _mft(item.label), offset: 2 }"
                @click="handleDockClick(item)"
                class="flex items-center justify-center w-full aspect-square rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-100 hover:border-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-150"
            >
                <i :class="['fa-light', item.icon, isEffectivelyExpanded ? 'text-base' : 'text-xs']"></i>
            </button>

            <button
                v-if="dockItems.length < maxDockSlots"
                @click="showCustomizer = true"
                class="flex items-center justify-center w-full rounded-xl border-2 border-dashed border-gray-200 border-transparent hover:border-gray-200 text-gray-300 hover:text-gray-500 transition-all duration-150"
                :class="dockItems.length > 0 ? 'aspect-square' : 'py-1'"
                :title="_mft('navigation:dock.addShortcut')"
            >
                <i class="fa-light fa-plus" :class="isEffectivelyExpanded ? 'text-base' : 'text-xs'"></i>
            </button>
            <button
                v-if="dockItems.length === maxDockSlots && isEffectivelyExpanded"
                @click="showCustomizer = true"
                class="absolute bottom-full right-2 w-5 h-5 z-modal flex items-center justify-center rounded-t-xl translate-y-px border border-b-transparent border-gray-200 bg-white text-gray-500 hover:text-gray-700 transition-all duration-150 opacity-0 group-hover:opacity-100"
                :title="_mft('navigation:dock.addShortcut')"
            >
                <i class="fa-light fa-pen text-[8px]"></i>
            </button>
        </div>

        <modal-basic
            :open="showCustomizer"
            @closed="showCustomizer = false"
        >
            <template #header>
                {{ _mft('navigation:dock.customize') }}
            </template>
            <template #body>
                <div v-for="category in dockCategoriesWithEntries" :key="category" class="mb-6">
                    <h3 class="text-sm font-semibold text-gray-700 mb-2">
                        {{ _mft(dockCategoryLabels[category]) }}
                    </h3>
                    <div class="grid grid-cols-4 gap-2">
                        <button
                            v-for="action in actionsForCategory(category)"
                            :key="action.key"
                            @click="isInDock(action) ? null : addToDock(action)"
                            class="flex flex-col items-center p-2 rounded-lg border transition-colors"
                            :class="isInDock(action) ? 'border-wlPrimary bg-wlPrimary/5 opacity-50 cursor-default' : 'border-gray-200 hover:border-gray-400 cursor-pointer'"
                        >
                            <i :class="['fa-light', action.icon, 'text-lg mb-1']"></i>
                            <span class="text-xs text-center truncate w-full">{{ _mft(action.label) }}</span>
                        </button>
                    </div>
                </div>

                <div v-if="dockItems.length > 0" class="mt-4 pt-4 border-t">
                    <h3 class="text-sm font-semibold text-gray-700 mb-2">
                        {{ _mft('navigation:dock.currentShortcuts') }}
                    </h3>
                    <div class="space-y-1">
                        <div
                            v-for="(item, i) in dockItems"
                            :key="item.key"
                            class="flex items-center justify-between p-2 bg-gray-50 rounded"
                        >
                            <div class="flex items-center gap-2">
                                <i :class="['fa-light', item.icon]"></i>
                                <span class="text-sm">{{ _mft(item.label) }}</span>
                            </div>
                            <button @click="removeFromDock(i)" class="text-gray-400 hover:text-red-500">
                                <i class="fa-light fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </modal-basic>
    </div>
</template>
